import { useEffect, useState } from "react";
import { UseVerConvocatorias } from "../../../hooks/UseVerConvocatorias";
import { useEncrypt } from "../../../hooks/useEncrypt";
import { useSeleccionarRoles } from "../../../hooks/useSeleccionarRoles";
import apiConvocatorias from "../../../api/Convocatorias/api-convocatorias";
import apiParticipantes from "../../../api/Participantes/api-participantes";
import {
  documentosEnBlanco,
  documentosSembrados,
  hayQueSembrar,
} from "../../../utils/sembrarDocumentos";
import { useSearchParams } from "react-router-dom";
import {
  PASOS,
  PRIMER_PASO,
  acotarPaso,
  esPrimerPaso,
  indiceDePaso,
  esUltimoPaso,
  perfilesHabilitados,
  tipoParaDocumentos,
} from "./pasosPostulacion";

/**
 * El estado del wizard de postulacion: en que paso va y con que perfil se
 * presenta la persona.
 *
 * Antes el paso era la ruta del navegador y el perfil viajaba por
 * localStorage entre seis paginas. Aqui el paso es estado de la pantalla y el
 * perfil se guarda una sola vez, en el formato que espera el resto del flujo.
 */
export const usePostulacionWizard = () => {
  const {
    getConvocatoriaPersistente,
    convocatoriaPersitente: convocatoria,
    descargarArchivo,
  } = UseVerConvocatorias();
  const { decrypt } = useEncrypt();
  const { asociarRoles } = useSeleccionarRoles();

  // Quien llega desde "continua con tu postulacion" entra directo a su paso.
  const [parametros, cambiarParametros] = useSearchParams();
  const [paso, setPaso] = useState<number>(() =>
    parametros.get("paso") ? indiceDePaso(parametros.get("paso")) : PRIMER_PASO
  );
  const [tipoParticipante, setTipoParticipante] = useState<number | null>(null);
  const [cargando, setCargando] = useState<boolean>(true);

  useEffect(() => {
    let vigente = true;
    const cargar = async () => {
      try {
        await getConvocatoriaPersistente();
      } catch (error) {
        console.error("No se pudo cargar la convocatoria de la postulación", error);
      } finally {
        if (vigente) setCargando(false);
      }
    };
    cargar();
    return () => {
      vigente = false;
    };
  }, []);

  /**
   * Recupera el perfil ya elegido, si la persona vuelve a entrar.
   *
   * Primero por localStorage, que es de donde lo lee el resto del flujo. Si
   * ahi no esta —otro navegador, o se limpio el almacenamiento— se pregunta
   * por la postulacion, que es el registro que manda, y se deja anotado.
   * Sin esto el modal de continuar podia decir que el perfil estaba puesto
   * y el wizard mostrar la pregunta sin responder.
   */
  useEffect(() => {
    const guardado = localStorage.getItem("tipo_participante_postulacion");
    const paraDocumentos = Number(guardado);
    if (guardado !== null && guardado !== "" && !Number.isNaN(paraDocumentos)) {
      setTipoParticipante(paraDocumentos + 1);
      return;
    }

    let vigente = true;

    const recuperarDelServidor = async () => {
      const idPostulacion = decrypt(localStorage.getItem("id_postulacion"));
      if (!idPostulacion) return;

      try {
        const respuesta: any = await apiParticipantes.consultarPostulacion(idPostulacion);
        const cuerpo = respuesta?.data ?? respuesta;
        const postulacion = Array.isArray(cuerpo) ? cuerpo[0] : cuerpo;
        const tipo = Number(postulacion?.tipo_participante);
        if (!vigente || !tipo || Number.isNaN(tipo)) return;

        setTipoParticipante(tipo);
        localStorage.setItem("tipo_participante_postulacion", tipoParaDocumentos(tipo));
      } catch (error) {
        console.error("No se pudo recuperar el perfil de la postulación", error);
      }
    };

    recuperarDelServidor();

    return () => {
      vigente = false;
    };
  }, []);

  /**
   * Cambia de paso y lo deja escrito en la dirección.
   *
   * Entrar por "continúa con tu postulación" ya traía el paso en la url, pero
   * al moverse se quedaba con el de entrada: la dirección decía una cosa y la
   * pantalla otra, así que recargar o compartir el enlace llevaba al paso
   * equivocado.
   *
   * Se reemplaza en vez de apilar, para que el botón de atrás del navegador
   * salga de la postulación y no recorra los pasos uno a uno.
   */
  const cambiarDePaso = (siguientePaso: number) => {
    const acotado = acotarPaso(siguientePaso);
    setPaso(acotado);

    const actualizados = new URLSearchParams(parametros);
    actualizados.set("paso", PASOS[acotado].clave);
    cambiarParametros(actualizados, { replace: true });
  };

  const irAPaso = (indice: number) => cambiarDePaso(indice);
  const siguiente = () => cambiarDePaso(paso + 1);
  const anterior = () => cambiarDePaso(paso - 1);

  /**
   * Deja creados en blanco los documentos que le corresponden al perfil.
   *
   * Si alguien no sube nada en la documentacion administrativa, cosa que se
   * permite porque es subsanable, la postulacion quedaba con la columna
   * vacia y despues no habia forma de saber que se le pidio. Sembrarlos deja
   * constancia. Se puede repetir sin consecuencias: si ya estan, no escribe.
   */
  const sembrarDocumentosDelPerfil = async (tipo: number) => {
    const idPostulacion = decrypt(localStorage.getItem("id_postulacion"));
    const idConvocatoria = decrypt(localStorage.getItem("idConvocatoriaPostulacion"));
    if (!idPostulacion || !idConvocatoria) return;

    const tipoPersona = Number(tipoParaDocumentos(tipo));

    try {
      const [administrativos, tecnicos, respuesta]: any[] = await Promise.all([
        apiConvocatorias.obtenerDocumentosAdministrativos(idConvocatoria),
        apiConvocatorias.obtenerDocumentosTecnicos(idConvocatoria),
        apiParticipantes.consultarPostulacion(idPostulacion),
      ]);

      const cuerpo = respuesta?.data ?? respuesta;
      const postulacion = Array.isArray(cuerpo) ? cuerpo[0] : cuerpo;

      const enBlancoAdmin = documentosEnBlanco(
        administrativos?.data?.documentos_administrativos,
        tipoPersona
      );
      const guardadosAdmin = postulacion?.documentos_administrativos ?? [];
      const resultadoAdmin = documentosSembrados(guardadosAdmin, enBlancoAdmin, tipoPersona);

      if (hayQueSembrar(guardadosAdmin, resultadoAdmin)) {
        await apiParticipantes.guardarDocumentosAdministrativosPostulacion(
          idPostulacion,
          resultadoAdmin
        );
      }

      const enBlancoTecnicos = documentosEnBlanco(
        tecnicos?.data?.documentos_tecnicos,
        tipoPersona
      );
      const guardadosTecnicos = postulacion?.documentos_tecnicos ?? [];
      const resultadoTecnicos = documentosSembrados(
        guardadosTecnicos,
        enBlancoTecnicos,
        tipoPersona
      );

      if (hayQueSembrar(guardadosTecnicos, resultadoTecnicos)) {
        await apiParticipantes.guardarDocumentosTecnicosPostulacion(
          idPostulacion,
          resultadoTecnicos
        );
      }
    } catch (error) {
      // No se interrumpe la postulacion por esto: los documentos se pueden
      // sembrar de nuevo la proxima vez que se elija el perfil.
      console.error("No se pudieron sembrar los documentos del perfil", error);
    }
  };

  /**
   * Guarda el perfil elegido.
   *
   * Son tres cosas, y el orden importa: el resto del flujo lee el tipo de
   * localStorage contando desde cero; asociarRoles crea la postulacion si
   * todavia no existe y deja el tipo en el registro del servidor; y solo
   * despues se pueden sembrar los documentos, que necesitan ese id.
   *
   * Esto lo hacia la pagina de seleccionar rol antes de mandar al
   * formulario del perfil. Aqui no se cambia de pantalla.
   */
  const cambiarTipoParticipante = async (tipo: number) => {
    setTipoParticipante(tipo);
    localStorage.setItem("tipo_participante_postulacion", tipoParaDocumentos(tipo));
    try {
      await asociarRoles(tipo, { navegar: false });
    } catch (error) {
      console.error("No se pudo asociar el perfil a la postulación", error);
    }
    await sembrarDocumentosDelPerfil(tipo);
  };

  return {
    convocatoria,
    descargarArchivo,
    cargando,
    perfiles: perfilesHabilitados(convocatoria),
    paso,
    irAPaso,
    siguiente,
    anterior,
    enPrimerPaso: esPrimerPaso(paso),
    enUltimoPaso: esUltimoPaso(paso),
    tipoParticipante,
    cambiarTipoParticipante,
    sembrarDocumentosDelPerfil,
  };
};
